import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowRight, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';


type Category = 'All' | 'AI' | 'Web Dev' | 'E-Commerce' | 'Blockchain' | 'Other';

interface Project {
  title: string;
  subtitle: string;
  description: string;
  gradientBg?: string;
  link: string;
  isInternal?: boolean;
  isPrivate?: boolean;
  featured?: boolean;
  category: Category;
  tags: string[];
}

const projects: Project[] = [
  {
    title: 'Custom AI Demo',
    subtitle: 'AI Chatbot',
    description: 'Interactive AI assistant showcasing real-world applications of large language models. Ask it anything about Elola Technologies or digital solutions.',
    gradientBg: 'linear-gradient(135deg, #1a0a3d 0%, #4b2aad 50%, #0d0628 100%)',
    link: '/chat',
    isInternal: true,
    featured: true,
    category: 'AI',
    tags: ['OpenAI', 'GPT', 'React'],
  },
  {
    title: 'Livermore Pallet Company',
    subtitle: 'Pallet Supplier · Bay Area',
    description: 'Fast, professional website for a Livermore-based pallet company — pallet removal, exchange, custom builds, and junk removal for Bay Area businesses.',
    gradientBg: 'linear-gradient(135deg, #1a0f05 0%, #6b3d0f 50%, #2e1a08 100%)',
    link: 'https://www.livermorepalletcompany.com/',
    featured: true,
    category: 'Web Dev',
    tags: ['React', 'Vite', 'Tailwind', 'Local Business'],
  },
  {
    title: 'CleanView USA',
    subtitle: 'Cleaning Services',
    description: 'Professional website for a cleaning services company — featuring service listings, instant quote requests, and a clean modern design built for local lead generation.',
    gradientBg: 'linear-gradient(135deg, #0a2a1a 0%, #1a6b3d 50%, #061a10 100%)',
    link: 'https://www.cleanviewusa.com/',
    featured: true,
    category: 'Web Dev',
    tags: ['React', 'Vite', 'Tailwind', 'Local Business'],
  },
  {
    title: 'Perfect Reflections',
    subtitle: 'Exotic Auto Body Shop',
    description: 'Premium website for a Bay Area exotic collision repair specialist serving Ferrari, Lamborghini, McLaren, Porsche, and Aston Martin owners since 1986.',
    gradientBg: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 50%, #1a2e4a 100%)',
    link: 'https://www.perfectreflections.us/',
    featured: true,
    category: 'Web Dev',
    tags: ['Web Design', 'Automotive', 'Local Business'],
  },
  {
    title: 'AI GPT Creations',
    subtitle: '166+ Custom GPTs',
    description: "Marcus S. Elola's growing library of 166+ custom GPTs with tens of thousands of combined uses — from Numerology to Rhyme Master and beyond.",
    gradientBg: 'linear-gradient(135deg, #0d1a3d 0%, #1e3a8a 50%, #091326 100%)',
    link: '/GPTs',
    isInternal: true,
    featured: true,
    category: 'AI',
    tags: ['GPT', 'OpenAI', 'AI Tools'],
  },
  {
    title: 'NFT Project Demo',
    subtitle: 'NFT Platform',
    description: 'A showcase of NFT technology demonstrating collection creation, minting, and marketplace integration built on Ethereum.',
    gradientBg: 'linear-gradient(135deg, #0f0f1a 0%, #3b1f6b 50%, #08080f 100%)',
    link: 'https://elola-nft.vercel.app/',
    category: 'Blockchain',
    tags: ['NFT', 'Ethereum', 'Web3'],
  },
  {
    title: 'Tri Valley Insurance Agency',
    subtitle: 'Independent Insurance Broker',
    description: 'Full-service independent insurance brokerage serving the East Bay — Livermore, Pleasanton, Dublin, and San Ramon. Offers auto, home, commercial, and life insurance with personalized guidance and access to multiple carriers.',
    gradientBg: 'linear-gradient(135deg, #0a1f0a 0%, #1a4a2a 40%, #0d3320 80%, #040f06 100%)',
    link: 'https://www.trivalley-insurance.com',
    featured: true,
    category: 'Web Dev',
    tags: ['Insurance', 'React', 'East Bay', 'Small Business'],
  },
  {
    title: 'Moxi Loxi',
    subtitle: 'Hair Extensions & Beauty',
    description: 'E-commerce website for a hair extensions brand — product showcase, brand identity, and online store built for a beauty entrepreneur.',
    gradientBg: 'linear-gradient(135deg, #2a0a1a 0%, #8b3a5a 50%, #1a0610 100%)',
    link: 'https://www.moxiloxi.com/',
    category: 'E-Commerce',
    tags: ['E-Commerce', 'Beauty', 'Web Design'],
  },
  {
    title: 'From Thrower to Pitcher',
    subtitle: 'E-Book Publishing',
    description: 'Published baseball instructional e-book distributed across Apple Books, Amazon Kindle, and other major platforms — handling the complete publishing pipeline.',
    gradientBg: 'linear-gradient(135deg, #2a1a0a 0%, #7a4f1e 50%, #1a0f05 100%)',
    link: 'https://books.apple.com/us/book/from-thrower-to-pitcher/id6739706431',
    category: 'Other',
    tags: ['Publishing', 'E-Book', 'Apple Books'],
  },
  {
    title: 'X-Ray Contractor',
    subtitle: 'Contractor SaaS Platform',
    description: 'Full-stack SaaS for construction contractors — bid management, estimates, invoices, CRM, timekeeping, payroll, job site tracking, and a platform admin portal with god mode for multi-tenant oversight.',
    gradientBg: 'linear-gradient(135deg, #0a1a2e 0%, #1a4a7a 50%, #061020 100%)',
    link: 'https://vbx-bid-automation.vercel.app/',
    featured: true,
    category: 'Web Dev',
    tags: ['Next.js 16', 'Supabase', 'Stripe', 'AI', 'Vercel', 'Resend', 'SaaS'],
  },
  {
    title: 'Express Tracker',
    subtitle: 'Auto Dealer Contract Platform',
    description: 'Web platform for auto dealerships to submit and track vehicle service contracts. Dealers submit contracts through a secure portal; admins monitor submissions across the full dealer network with PDF and Excel export.',
    gradientBg: 'linear-gradient(135deg, #0f1a0a 0%, #2a5c1a 50%, #081009 100%)',
    link: '#',
    isPrivate: true,
    featured: true,
    category: 'Web Dev',
    tags: ['React 19', 'Vite', 'Firebase', 'Tailwind', 'Auto Industry'],
  },
  {
    title: 'preDash',
    subtitle: 'AI Business Dashboard',
    description: 'AI-powered business document platform — company profile management, PDF and document storage, and an AI chat interface for querying business documents and getting instant company-specific insights.',
    gradientBg: 'linear-gradient(135deg, #1a0f2e 0%, #4a2a7a 50%, #100820 100%)',
    link: '#',
    isPrivate: true,
    featured: true,
    category: 'AI',
    tags: ['React 19', 'Vite', 'Firebase', 'OpenAI', 'Tailwind'],
  },
];

const categories: Category[] = ['All', 'AI', 'Web Dev', 'E-Commerce', 'Blockchain', 'Other'];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Projects: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<Category>('All');

  const filtered = (activeCategory === 'All'
    ? projects
    : projects.filter(p => p.category === activeCategory)
  ).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <>
      <Helmet>
        <title>Projects | Elola Technologies – AI, Web3, NFT & Web Development</title>
        <meta name="description" content="Explore Elola Technologies' portfolio of impactful work spanning AI tools, e-commerce, Web3, NFTs, web development, and e-book publishing." />
        <link rel="canonical" href="https://elola.tech/projects" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Elola Technologies" />
        <meta property="og:title" content="Projects | Elola Technologies – Portfolio" />
        <meta property="og:description" content="See Elola Technologies' portfolio spanning AI, Web3, NFTs, web development, and more." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://elola.tech/projects" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Projects | Elola Technologies – Portfolio" />
        <meta name="twitter:description" content="See Elola Technologies' portfolio spanning AI, Web3, NFTs, web development, and more." />
        <meta name="twitter:image" content="https://elola.tech/preview-image.png" />
      </Helmet>

      <div style={{ background: 'var(--bg-base)', minHeight: '100vh', paddingTop: 'var(--navbar-height)' }}>
        {/* Header */}
        <section style={{
          padding: 'clamp(50px, 7vw, 90px) var(--page-padding-x) clamp(40px, 5vw, 70px)',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
          borderBottom: '1px solid var(--border-card)',
        }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Briefcase size={13} />
              Our Work
            </div>
            <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 800, marginBottom: '16px' }}>
              Projects We're <span className="gradient-text">Proud Of</span>
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'clamp(15px, 1.5vw, 17px)', maxWidth: '500px', margin: '0 auto' }}>
              From AI tools to e-commerce platforms, a selection of the work we've built for clients and our own initiatives.
            </p>
          </motion.div>
        </section>

        {/* Filter Tabs */}
        <div style={{
          padding: '28px var(--page-padding-x) 0',
          maxWidth: 'var(--page-max-width)',
          margin: '0 auto',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '8px 18px',
                borderRadius: '50px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${activeCategory === cat ? 'var(--accent-purple)' : 'var(--border-card)'}`,
                background: activeCategory === cat ? 'rgba(109, 74, 255, 0.15)' : 'transparent',
                color: activeCategory === cat ? 'var(--accent-purple-light)' : 'var(--text-secondary)',
                transition: 'all 0.2s',
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <section style={{ padding: 'clamp(32px, 4vw, 48px) var(--page-padding-x) clamp(64px, 8vw, 100px)', maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
          <motion.div
            key={activeCategory}
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 'var(--card-gap)',
            }}
          >
            {filtered.map((project) => (
              <motion.div
                key={project.title}
                variants={itemVariants}
                whileHover={{ y: -5, boxShadow: project.featured ? '0 16px 48px rgba(245,158,11,0.25)' : '0 16px 48px rgba(109,74,255,0.2)' }}
                style={{
                  background: 'var(--bg-card)',
                  border: project.featured ? '1px solid rgba(245,158,11,0.45)' : '1px solid var(--border-card)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  boxShadow: project.featured ? '0 0 18px rgba(245,158,11,0.08)' : 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = project.featured ? 'rgba(245,158,11,0.75)' : 'var(--border-subtle)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = project.featured ? 'rgba(245,158,11,0.45)' : 'var(--border-card)')}
              >
                {/* Gradient Banner */}
                <div style={{ aspectRatio: '16/9', overflow: 'hidden', position: 'relative', background: project.gradientBg || 'var(--bg-surface)' }}>
                  <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{
                      fontSize: 'clamp(14px, 2vw, 20px)',
                      fontWeight: 800,
                      color: 'rgba(255,255,255,0.75)',
                      textAlign: 'center',
                      padding: '0 20px',
                      letterSpacing: '-0.02em',
                    }}>
                      {project.title}
                    </span>
                  </div>
                  <div style={{
                    position: 'absolute', top: '12px', left: '12px',
                    background: 'rgba(8,8,15,0.85)', backdropFilter: 'blur(8px)',
                    border: '1px solid var(--border-card)',
                    padding: '4px 10px', borderRadius: '50px',
                    fontSize: '11px', fontWeight: 600, color: 'var(--accent-purple-light)',
                  }}>
                    {project.category}
                  </div>
                  {project.featured && (
                    <div style={{
                      position: 'absolute', top: '12px', right: '12px',
                      background: 'rgba(245,158,11,0.15)', backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(245,158,11,0.4)',
                      padding: '4px 10px', borderRadius: '50px',
                      fontSize: '11px', fontWeight: 700, color: '#f5a623',
                      letterSpacing: '0.04em',
                    }}>
                      Featured
                    </div>
                  )}
                </div>

                {/* Content */}
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                  <div>
                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>
                      {project.subtitle}
                    </p>
                    <h3 style={{ fontSize: '17px', fontWeight: 700 }}>{project.title}</h3>
                  </div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6', flex: 1 }}>
                    {project.description}
                  </p>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {project.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: '11px', fontWeight: 500,
                        padding: '3px 10px', borderRadius: '50px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid var(--border-card)',
                        color: 'var(--text-muted)',
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: '6px' }}>
                    {project.isPrivate ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '9px 18px', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', border: '1px solid var(--border-card)', borderRadius: '8px' }}>
                        Private Project
                      </span>
                    ) : project.isInternal ? (
                      <Link to={project.link} className="btn btn-outline" style={{ padding: '9px 18px', fontSize: '13px' }}>
                        <ArrowRight size={14} />
                        View Project
                      </Link>
                    ) : (
                      <a href={project.link} target="_blank" rel="noopener noreferrer" className="btn btn-outline" style={{ padding: '9px 18px', fontSize: '13px' }}>
                        <ExternalLink size={14} />
                        Visit Site
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* CTA */}
        <section style={{
          padding: 'clamp(50px, 6vw, 80px) var(--page-padding-x)',
          background: 'linear-gradient(135deg, rgba(109,74,255,0.08) 0%, rgba(90,63,224,0.04) 100%)',
          borderTop: '1px solid var(--border-subtle)',
          textAlign: 'center',
        }}>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800, marginBottom: '14px' }}>
              Ready to add your project here?
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '28px', maxWidth: '440px', margin: '0 auto 28px' }}>
              Let's build something that makes you proud. Every great project starts with a conversation.
            </p>
            <Link to="/contact" className="btn btn-primary" style={{ fontSize: '16px', padding: '14px 32px' }}>
              <ArrowRight size={16} />
              Start a Project
            </Link>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Projects;
